import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, caisseAPI } from '../../services/api';
import BalanceCard from './BalanceCard';
import CustomSnackbar from '../../components/ui/Snackbar';
import Rows from '../../components/ui/Rows';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import { formatAmountForDisplay } from '../../utils/amountFormatter';

const Dashboard = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCaisseIds, setSelectedCaisseIds] = useState([]);
  const [isCaisseDropdownOpen, setIsCaisseDropdownOpen] = useState(false);
  const caisseDropdownScrollRef = useRef(null);
  const [caisseOptions, setCaisseOptions] = useState([]);
  const [physiqueCaisses, setPhysiqueCaisses] = useState([]);
  const [mobileCaisses, setMobileCaisses] = useState([]);
  const [caisseBalances, setCaisseBalances] = useState({ caisse: 0, coffre: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Lists data state
  const [categoriesData, setCategoriesData] = useState([]);
  const [coffreTransactionsData, setCoffreTransactionsData] = useState([]);
  
  // Date range state for both lists
  const [categoriesDateRange, setCategoriesDateRange] = useState(null);
  const [coffreDateRange, setCoffreDateRange] = useState(null);
  
  // State for sorting
  const [categoriesSortBy, setCategoriesSortBy] = useState('categoryName');
  const [categoriesSortDirection, setCategoriesSortDirection] = useState('ASC');
  const [coffreSortBy, setCoffreSortBy] = useState('createdAt');
  const [coffreSortDirection, setCoffreSortDirection] = useState('DESC');
  const [commercialSortBy, setCommercialSortBy] = useState('commercial');
  const [commercialSortDirection, setCommercialSortDirection] = useState('ASC');
  
  // Scroll preservation for caisse dropdown
  const prevSelectedCaisseIdsRef = useRef(selectedCaisseIds);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.__dashboardCaisseScrollPosition) {
      window.__dashboardCaisseScrollPosition = null;
    }
    
    const handleScroll = () => {
      if (caisseDropdownScrollRef.current) {
        window.__dashboardCaisseScrollPosition = caisseDropdownScrollRef.current.scrollTop;
      }
    };
    
    if (caisseDropdownScrollRef.current && isCaisseDropdownOpen) {
      const scrollElement = caisseDropdownScrollRef.current;
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isCaisseDropdownOpen]);
  
  useEffect(() => {
    if (!isCaisseDropdownOpen) {
      window.__dashboardCaisseScrollPosition = null;
    }
  }, [isCaisseDropdownOpen]);
  
  useLayoutEffect(() => {
    const selectionsChanged = JSON.stringify(prevSelectedCaisseIdsRef.current) !== JSON.stringify(selectedCaisseIds);
    
    if (selectionsChanged && isCaisseDropdownOpen && caisseDropdownScrollRef.current && window.__dashboardCaisseScrollPosition !== null) {
      prevSelectedCaisseIdsRef.current = selectedCaisseIds;
      
      caisseDropdownScrollRef.current.scrollTop = window.__dashboardCaisseScrollPosition;
      
      const timeout1 = setTimeout(() => {
        if (caisseDropdownScrollRef.current) {
          caisseDropdownScrollRef.current.scrollTop = window.__dashboardCaisseScrollPosition;
        }
      }, 0);
      
      const timeout2 = setTimeout(() => {
        if (caisseDropdownScrollRef.current) {
          caisseDropdownScrollRef.current.scrollTop = window.__dashboardCaisseScrollPosition;
        }
      }, 10);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    } else if (selectionsChanged) {
      prevSelectedCaisseIdsRef.current = selectedCaisseIds;
    }
  }, [selectedCaisseIds, isCaisseDropdownOpen]);


  // Function to get caisse-specific data from the already loaded data
  const getCaisseData = useCallback((caisseId) => {
    if (!dashboardData?.caisses) return { 
      caisse: 0, 
      coffre: 0
    };
    
    const selectedCaisseData = dashboardData.caisses.find(caisse => caisse.id === caisseId);
    
    if (!selectedCaisseData) return { 
      caisse: 0, 
      coffre: 0
    };
    
    return {
      caisse: selectedCaisseData.caisseBalance || 0,
      coffre: selectedCaisseData.coffreBalance || 0
    };
  }, [dashboardData]);

  // Fetch categories data
  const fetchCategoriesData = useCallback(async (caisseIds, dateRange) => {
    if (!caisseIds || (Array.isArray(caisseIds) && caisseIds.length === 0)) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      let response;
      if (Array.isArray(caisseIds) && caisseIds.length > 1) {
        // Multiple caisses
        response = await dashboardAPI.getMultipleCaissesCategoriesSummary(caisseIds, fromDate, toDate, categoriesSortBy, categoriesSortDirection);
      } else {
        // Single caisse
        const caisseId = Array.isArray(caisseIds) ? caisseIds[0] : caisseIds;
        response = await dashboardAPI.getCategoriesSummary(caisseId, fromDate, toDate, categoriesSortBy, categoriesSortDirection);
      }
      
      const categories = response.data.categories || [];
      
      // Transform data for display
      const transformedData = categories.map(category => {
        const isMixed = category.caisseFactor !== 0 && category.coffreFactor !== 0;
        const isNeutral = category.caisseFactor === 0 && category.coffreFactor === 0;
        
        let amountDisplay;
        let amountColor;
        
        // Backend sends effective amounts (already multiplied by factor)
        if (isMixed || isNeutral) {
          // Mixed or neutral transactions - no sign
          amountDisplay = formatAmountForDisplay(category.sum, false).text;
          amountColor = '#027BC6'; // Blue for neutral/mixed
        } else {
          // Pure transactions - effective amount with sign
          const formatted = formatAmountForDisplay(category.sum, true);
          amountDisplay = formatted.text;
          amountColor = formatted.color;
        }
        
        return {
          categoryName: category.categoryName,
          sum: amountDisplay,
          isMixed,
          isNeutral,
          amountColor,
          categoryDetailIds: category.categoryDetailIds || []
        };
      });
      
      setCategoriesData(transformedData);
    } catch (error) {
      console.error('Error fetching categories data:', error);
      setCategoriesData([]);
    }
  }, [categoriesSortBy, categoriesSortDirection]);

  // Fetch coffre transactions data
  const fetchCoffreTransactionsData = useCallback(async (caisseIds, dateRange) => {
    if (!caisseIds || (Array.isArray(caisseIds) && caisseIds.length === 0)) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      // Handle both single caisseId and array of caisseIds
      const ids = Array.isArray(caisseIds) ? caisseIds : [caisseIds];
      
      // Fetch transactions for all selected caisses
      const allTransactions = [];
      for (const caisseId of ids) {
        try {
          const response = await dashboardAPI.getCoffreTransactions(caisseId, fromDate, toDate, coffreSortBy, coffreSortDirection);
          const transactions = response.data || [];
          allTransactions.push(...transactions);
        } catch (error) {
          console.error(`Error fetching coffre transactions for caisse ${caisseId}:`, error);
        }
      }
      
      // Sort all transactions by date (descending) and take last 10
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt || 0);
        const dateB = new Date(b.date || b.createdAt || 0);
        return dateB - dateA; // Descending order
      });
      
      // Take the last 10 entries (most recent)
      const last10Transactions = allTransactions.slice(0, 10);
      
      // Transform data for display
      const transformedData = last10Transactions.map(transaction => {
        const factor = transaction.factor || 1;
        const adjustedAmount = transaction.amount * factor;
        const formatted = formatAmountForDisplay(adjustedAmount, true);
        
        return {
          createdAt: transaction.date,
          amount: formatted.text,
          amountColor: formatted.color,
          agentName: transaction.by
        };
      });
      
      setCoffreTransactionsData(transformedData);
    } catch (error) {
      console.error('Error fetching coffre transactions data:', error);
      setCoffreTransactionsData([]);
    }
  }, [coffreSortBy, coffreSortDirection]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (user.role === 'AGENT') {
          const assigned = user.assignedCaisses && user.assignedCaisses[0] ? user.assignedCaisses[0] : null;
          if (assigned) {
            const option = { id: assigned.id, name: assigned.name, type: assigned.type };
            setCaisseOptions([option]);
            setSelectedCaisseIds([assigned.id]);
            
            const resp = await dashboardAPI.getCaisseDashboard(assigned.id);
            const data = resp.data || {};
            setDashboardData({ caisses: [{ id: assigned.id, name: assigned.name, caisseBalance: data.caisseBalance || 0, coffreBalance: data.coffreBalance || 0 }] });
          }
        } else {
          const response = await caisseAPI.getForFilters();
          const caisses = Array.isArray(response.data) ? response.data : [];
          
          const physiques = caisses
            .filter(c => c.type === 'PHYSIQUE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'PHYSIQUE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const mobiles = caisses
            .filter(c => c.type === 'MOBILE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'MOBILE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          setPhysiqueCaisses(physiques);
          setMobileCaisses(mobiles);
          
          const allCaisses = [...physiques, ...mobiles];
          setCaisseOptions(allCaisses);
          
          const firstPhysiqueCaisse = physiques.length > 0 ? physiques[0] : (allCaisses.length > 0 ? allCaisses[0] : null);
          if (firstPhysiqueCaisse) {
            setSelectedCaisseIds([firstPhysiqueCaisse.id]);
            
            const dashboardResponse = await dashboardAPI.getAllCaissesDashboard(null, [firstPhysiqueCaisse.id]);
            setDashboardData(dashboardResponse.data);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Échec du chargement des données du tableau de bord');
        setSnackbar({
          open: true,
          message: 'Erreur lors du chargement des données du tableau de bord',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Load dashboard data when selected caisses change
  useEffect(() => {
    const loadDashboardData = async () => {
      if (user?.role === 'AGENT') {
        if (selectedCaisseIds.length > 0) {
          const caisseId = selectedCaisseIds[0];
          try {
            const resp = await dashboardAPI.getCaisseDashboard(caisseId);
            const data = resp.data || {};
            const assigned = user.assignedCaisses?.[0];
            setDashboardData({ 
              caisses: [{ 
                id: caisseId, 
                name: assigned?.name || '', 
                caisseBalance: data.caisseBalance || 0, 
                coffreBalance: data.coffreBalance || 0 
              }] 
            });
          } catch (err) {
            console.error('Error loading dashboard data:', err);
          }
        }
      } else if (user?.role === 'MANAGER' && selectedCaisseIds.length > 0) {
        try {
          const dashboardResponse = await dashboardAPI.getAllCaissesDashboard(null, selectedCaisseIds);
          setDashboardData(dashboardResponse.data);
        } catch (err) {
          console.error('Error loading dashboard data:', err);
        }
      }
    };

    if (user && selectedCaisseIds.length > 0) {
      loadDashboardData();
    }
  }, [user, selectedCaisseIds]);

  // Update caisse balances when dashboard data changes
  useEffect(() => {
    if (dashboardData?.caisses && selectedCaisseIds.length > 0) {
      if (selectedCaisseIds.length === 1) {
        const caisseData = getCaisseData(selectedCaisseIds[0]);
        setCaisseBalances({
          caisse: caisseData.caisse,
          coffre: caisseData.coffre
        });
      } else {
        let totalCaisse = 0;
        let totalCoffre = 0;
        selectedCaisseIds.forEach(id => {
          const data = getCaisseData(id);
          totalCaisse += data.caisse;
          totalCoffre += data.coffre;
        });
        setCaisseBalances({
          caisse: totalCaisse,
          coffre: totalCoffre
        });
      }
    }
  }, [dashboardData, selectedCaisseIds, getCaisseData]);

  // Check if multiple mobile caisses are selected
  const isMultipleMobileCaissesSelected = useMemo(() => {
    if (user?.role !== 'MANAGER' || selectedCaisseIds.length === 0 || mobileCaisses.length === 0) {
      return false;
    }
    const selectedMobileCount = selectedCaisseIds.filter(id => 
      mobileCaisses.some(c => c.id === id)
    ).length;
    return selectedMobileCount > 1;
  }, [selectedCaisseIds, mobileCaisses, user?.role]);

  // Fetch lists data when caisse changes
  useEffect(() => {
    if (selectedCaisseIds.length > 0) {
      if (isMultipleMobileCaissesSelected) {
        // For multiple mobile caisses, only fetch categories (coffre transactions will be fetched separately)
        fetchCategoriesData(selectedCaisseIds, categoriesDateRange);
        // Still fetch coffre transactions for all selected caisses
        fetchCoffreTransactionsData(selectedCaisseIds, coffreDateRange);
      } else {
        fetchCategoriesData(selectedCaisseIds, categoriesDateRange);
        // Fetch coffre transactions for all selected caisses
        fetchCoffreTransactionsData(selectedCaisseIds, coffreDateRange);
      }
    } else {
      setCategoriesData([]);
      setCoffreTransactionsData([]);
    }
  }, [selectedCaisseIds, categoriesDateRange, coffreDateRange, categoriesSortBy, categoriesSortDirection, coffreSortBy, coffreSortDirection, fetchCategoriesData, fetchCoffreTransactionsData, isMultipleMobileCaissesSelected]);

  // Handle clicking outside caisse dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCaisseDropdownOpen) {
        // Check if click is outside the dropdown
        const dropdownElement = event.target.closest('[data-caisse-dropdown]');
        if (!dropdownElement) {
          setIsCaisseDropdownOpen(false);
        }
      }
    };

    if (isCaisseDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCaisseDropdownOpen]);

  // Date range change handlers
  const handleCategoriesDateRangeChange = (dateRange) => {
    setCategoriesDateRange(dateRange);
    if (selectedCaisseIds.length > 0) {
      fetchCategoriesData(selectedCaisseIds, dateRange);
    }
  };

  const handleCoffreDateRangeChange = (dateRange) => {
    setCoffreDateRange(dateRange);
    if (selectedCaisseIds.length > 0) {
      fetchCoffreTransactionsData(selectedCaisseIds, dateRange);
    }
  };

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  // Header height is approximately 76px, so Dashboard sticky header starts below it
  const HEADER_HEIGHT = 76;

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  return (
    <Box>
      {/* Sticky Header Section - Always render to prevent line during loading */}
      <Box
        sx={{
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          zIndex: 999,
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(248, 248, 248, 1), #F8F8F8)'
        }}
      >
        <Box sx={{ ...contentContainerSx, paddingTop: '24px', paddingBottom: '1px' }}>
          <Box sx={{ marginBottom: '8px', width: '100%' }}>
            <h1 
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '32px',
                letterSpacing: '0px',
                color: '#38485C',
                opacity: 0.88,
                textTransform: 'none',
                margin: 0
              }}
            >
              Tableau de Bord
            </h1>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              marginBottom: '24px'
            }}
          >
          <Box position="relative" data-caisse-dropdown>
            <Box 
              display="flex" 
              alignItems="center" 
              sx={{ cursor: user.role !== 'AGENT' ? 'pointer' : 'default' }}
              onClick={() => { if (user.role !== 'AGENT') setIsCaisseDropdownOpen(!isCaisseDropdownOpen); }}
            >
              <Typography 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  letterSpacing: '0px',
                  color: '#027BC6',
                  textTransform: 'none',
                  margin: 0,
                  marginRight: '8px'
                }}
              >
                {(() => {
                  if (user.role === 'AGENT') {
                    return caisseOptions.length > 0 ? caisseOptions[0].name : 'Bouskoura';
                  }
                  if (selectedCaisseIds.length === 0) {
                    const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : (caisseOptions.length > 0 ? caisseOptions[0] : null);
                    return firstPhysique ? firstPhysique.name : 'Sélectionner une caisse';
                  } else if (selectedCaisseIds.length === 1) {
                    const selected = caisseOptions.find(c => c.id === selectedCaisseIds[0]);
                    return selected?.name || 'Sélectionner une caisse';
                  } else {
                    return `${selectedCaisseIds.length} caisses sélectionnées`;
                  }
                })()}
              </Typography>
              {user.role !== 'AGENT' && (
                <img 
                  src={chevronDownIcon} 
                  alt="Chevron Down" 
                  className={`h-3 w-3 transform transition-transform duration-200 ${
                    isCaisseDropdownOpen ? 'rotate-180' : ''
                  }`}
                  style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)' }}
                />
              )}
            </Box>
            
            {isCaisseDropdownOpen && user.role !== 'AGENT' && (
              <Box
                key={`caisse-dropdown-${selectedCaisseIds.join('-')}`}
                ref={caisseDropdownScrollRef}
                position="absolute"
                top="100%"
                left={0}
                mt={1}
                bgcolor="white"
                border="1px solid #e5e7eb"
                borderRadius="8px"
                boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                zIndex={1200}
                minWidth="250px"
                maxHeight="400px"
                overflow="auto"
              >
                {user?.role === 'MANAGER' && (
                  <>
                    {(() => {
                      const allMobileIds = mobileCaisses.map(c => c.id);
                      const allMobileSelected = allMobileIds.length > 0 && 
                        allMobileIds.every(id => selectedCaisseIds.includes(id));
                      return (
                        <Box
                          px={2}
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          style={{
                            cursor: 'pointer',
                            backgroundColor: allMobileSelected ? '#f0f9ff' : 'transparent',
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                          onMouseEnter={(e) => {
                            if (!allMobileSelected) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = allMobileSelected ? '#f0f9ff' : 'transparent';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (allMobileSelected) {
                              const remainingIds = selectedCaisseIds.filter(id => !allMobileIds.includes(id));
                              if (remainingIds.length === 0) {
                                const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : (caisseOptions.length > 0 ? caisseOptions[0] : null);
                                if (firstPhysique) {
                                  setSelectedCaisseIds([firstPhysique.id]);
                                }
                              } else {
                                setSelectedCaisseIds(remainingIds);
                              }
                            } else {
                              const physiqueIds = physiqueCaisses.map(c => c.id);
                              const newIds = selectedCaisseIds.filter(id => !physiqueIds.includes(id));
                              allMobileIds.forEach(id => {
                                if (!newIds.includes(id)) {
                                  newIds.push(id);
                                }
                              });
                              setSelectedCaisseIds(newIds);
                            }
                          }}
                        >
                          <span style={{ 
                            color: '#027BC6', 
                            fontSize: '16px',
                            fontWeight: 700,
                            lineHeight: '1',
                            width: '16px',
                            display: 'inline-block',
                            marginRight: '8px'
                          }}>
                            {allMobileSelected ? '✓' : ''}
                          </span>
                          <Typography
                            style={{
                              fontFamily: 'Montserrat',
                              fontSize: '14px',
                              fontWeight: 700,
                              color: '#027BC6',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Toutes les caisses mobiles
                          </Typography>
                        </Box>
                      );
                    })()}
                    
                    {(() => {
                      const allPhysiqueIds = physiqueCaisses.map(c => c.id);
                      const allPhysiqueSelected = allPhysiqueIds.length > 0 && 
                        allPhysiqueIds.every(id => selectedCaisseIds.includes(id));
                      return (
                        <Box
                          px={2}
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          style={{
                            cursor: 'pointer',
                            backgroundColor: allPhysiqueSelected ? '#f0f9ff' : 'transparent',
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                          onMouseEnter={(e) => {
                            if (!allPhysiqueSelected) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = allPhysiqueSelected ? '#f0f9ff' : 'transparent';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (allPhysiqueSelected) {
                              const remainingIds = selectedCaisseIds.filter(id => !allPhysiqueIds.includes(id));
                              if (remainingIds.length === 0) {
                                const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : (caisseOptions.length > 0 ? caisseOptions[0] : null);
                                if (firstPhysique) {
                                  setSelectedCaisseIds([firstPhysique.id]);
                                }
                              } else {
                                setSelectedCaisseIds(remainingIds);
                              }
                            } else {
                              const mobileIds = mobileCaisses.map(c => c.id);
                              const newIds = selectedCaisseIds.filter(id => !mobileIds.includes(id));
                              allPhysiqueIds.forEach(id => {
                                if (!newIds.includes(id)) {
                                  newIds.push(id);
                                }
                              });
                              setSelectedCaisseIds(newIds);
                            }
                          }}
                        >
                          <span style={{ 
                            color: '#027BC6', 
                            fontSize: '16px',
                            fontWeight: 700,
                            lineHeight: '1',
                            width: '16px',
                            display: 'inline-block',
                            marginRight: '8px'
                          }}>
                            {allPhysiqueSelected ? '✓' : ''}
                          </span>
                          <Typography
                            style={{
                              fontFamily: 'Montserrat',
                              fontSize: '14px',
                              fontWeight: 700,
                              color: '#027BC6',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Toutes les caisses physiques
                          </Typography>
                        </Box>
                      );
                    })()}
                    
                    {caisseOptions.length > 0 && (
                      <Box sx={{ borderBottom: '1px solid #e5e7eb', py: 0.5 }} />
                    )}
                  </>
                )}
                
                {caisseOptions.map((caisse) => {
                  const isSelected = selectedCaisseIds.includes(caisse.id);
                  return (
                    <Box
                      key={caisse.id}
                      px={2}
                      py={1.5}
                      display="flex"
                      alignItems="center"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                        fontFamily: 'Montserrat',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isSelected ? '#f0f9ff' : 'transparent';
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                          setSelectedCaisseIds(prev => {
                            const newIds = prev.filter(id => id !== caisse.id);
                            if (newIds.length === 0) {
                              const firstPhysique = physiqueCaisses.length > 0 ? physiqueCaisses[0] : (caisseOptions.length > 0 ? caisseOptions[0] : null);
                              return firstPhysique ? [firstPhysique.id] : [];
                            }
                            return newIds;
                          });
                        } else {
                          const isMobile = caisse.type === 'MOBILE';
                          const isPhysique = caisse.type === 'PHYSIQUE';
                          
                          setSelectedCaisseIds(prev => {
                            let newIds = [...prev];
                            
                            if (isMobile) {
                              const physiqueIds = physiqueCaisses.map(c => c.id);
                              newIds = newIds.filter(id => !physiqueIds.includes(id));
                            }
                            
                            if (isPhysique) {
                              const mobileIds = mobileCaisses.map(c => c.id);
                              newIds = newIds.filter(id => !mobileIds.includes(id));
                            }
                            
                            if (!newIds.includes(caisse.id)) {
                              newIds.push(caisse.id);
                            }
                            
                            return newIds;
                          });
                        }
                      }}
                    >
                      <span style={{ 
                        color: '#027BC6', 
                        fontSize: '16px',
                        fontWeight: 700,
                        lineHeight: '1',
                        width: '16px',
                        display: 'inline-block',
                        marginRight: '8px'
                      }}>
                        {isSelected ? '✓' : ''}
                      </span>
                      <Typography
                        style={{
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? '#027BC6' : '#38485C',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {caisse.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>

          <Box 
            display="flex" 
            gap={3} 
            mb={5}
            sx={{ width: '100%', flexWrap: 'wrap' }}
          >
            <Box sx={{ flex: 1, minWidth: '240px' }}>
              <BalanceCard
                title="Solde Caisse"
                amount={formatAmountForDisplay(caisseBalances.caisse, false).text}
                color="blue"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: '240px' }}>
              <BalanceCard
                title="Solde Coffre"
                amount={formatAmountForDisplay(caisseBalances.coffre, false).text}
                color="green"
              />
            </Box>
          </Box>

          {/* Table Titles and Headers */}
          <Box 
            display="flex" 
            gap={4} 
            mb={2}
            sx={{ width: '100%', flexWrap: 'wrap' }}
          >
            {/* Left Table Header */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}
              >
                <Typography
                  style={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '32px',
                    letterSpacing: '0px',
                    color: '#38485C',
                    opacity: 0.88,
                    textTransform: 'none',
                    margin: 0
                  }}
                >
                  {isMultipleMobileCaissesSelected ? 'Solde par commercial' : 'Sommes par catégorie'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  color: '#919191',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  backgroundColor: '#F8F8F8',
                  marginTop: '0px'
                }}
              >
                {isMultipleMobileCaissesSelected ? (
                  <>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (commercialSortBy === 'commercial' && commercialSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCommercialSortBy('commercial');
                      setCommercialSortDirection(newDirection);
                    }}>
                      <span>Commercial</span>
                      {commercialSortBy === 'commercial' && (
                        <img 
                          src={commercialSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                          alt="sort" 
                          style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (commercialSortBy === 'solde' && commercialSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCommercialSortBy('solde');
                      setCommercialSortDirection(newDirection);
                    }}>
                      <span>Solde</span>
                      {commercialSortBy === 'solde' && (
                        <img 
                          src={commercialSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                          alt="sort" 
                          style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (categoriesSortBy === 'categoryName' && categoriesSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCategoriesSortBy('categoryName');
                      setCategoriesSortDirection(newDirection);
                    }}>
                      <span>Catégories</span>
                      {categoriesSortBy === 'categoryName' && (
                        <img 
                          src={categoriesSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                          alt="sort" 
                          style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (categoriesSortBy === 'sum' && categoriesSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCategoriesSortBy('sum');
                      setCategoriesSortDirection(newDirection);
                    }}>
                      <span>Somme</span>
                      {categoriesSortBy === 'sum' && (
                        <img 
                          src={categoriesSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                          alt="sort" 
                          style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {/* Right Table Header */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}
              >
                <Typography
                  style={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '32px',
                    letterSpacing: '0px',
                    color: '#38485C',
                    opacity: 0.88,
                    textTransform: 'none',
                    margin: 0
                  }}
                >
                  Entrées/Sorties Coffre
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  color: '#919191',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  backgroundColor: '#F8F8F8',
                  marginTop: '0px',
                  marginBottom: '0px'
                }}
              >
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'createdAt' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('createdAt');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Date</span>
                  {coffreSortBy === 'createdAt' && (
                    <img 
                      src={coffreSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                      alt="sort" 
                      style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'amount' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('amount');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Montant</span>
                  {coffreSortBy === 'amount' && (
                    <img 
                      src={coffreSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                      alt="sort" 
                      style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'agentName' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('agentName');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Par</span>
                  {coffreSortBy === 'agentName' && (
                    <img 
                      src={coffreSortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon} 
                      alt="sort" 
                      style={{ marginLeft: '4px', width: '12px', height: '12px' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ ...contentContainerSx, paddingTop: '32px', paddingBottom: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box 
          display="flex" 
          gap={4} 
          mb={6}
          sx={{ width: '100%', flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            {isMultipleMobileCaissesSelected ? (
              <Rows
                title=""
                hideHeader={true}
                showDatePicker={false}
                columns={[
                  { key: 'commercial', label: 'Commercial', sortable: true },
                  { 
                    key: 'solde', 
                    label: 'Solde',
                    sortable: true,
                    render: (amount) => (
                      <span style={{ 
                        color: '#027BC6',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        lineHeight: '19px',
                        letterSpacing: '0px',
                        textTransform: 'none',
                        opacity: 1
                      }}>
                        {amount}
                      </span>
                    )
                  }
                ]}
                data={(() => {
                  if (!dashboardData?.caisses) return [];
                  const commercialData = dashboardData.caisses
                    .filter(caisse => mobileCaisses.some(mc => mc.id === caisse.id))
                    .map(caisse => ({
                      commercial: caisse.name,
                      solde: formatAmountForDisplay(caisse.caisseBalance || 0, false).text,
                      soldeValue: typeof caisse.caisseBalance === 'number' ? caisse.caisseBalance : parseFloat(caisse.caisseBalance || 0)
                    }));
                  
                  return commercialData.sort((a, b) => {
                    let comparison = 0;
                    if (commercialSortBy === 'commercial') {
                      comparison = a.commercial.localeCompare(b.commercial);
                    } else if (commercialSortBy === 'solde') {
                      comparison = a.soldeValue - b.soldeValue;
                    }
                    return commercialSortDirection === 'ASC' ? comparison : -comparison;
                  });
                })()}
                maxHeight="500px"
                sortBy={commercialSortBy}
                sortDirection={commercialSortDirection}
                onSort={(sortBy, sortDirection) => {
                  setCommercialSortBy(sortBy);
                  setCommercialSortDirection(sortDirection);
                }}
                containerPadding={0}
              />
            ) : (
              <Rows
                title=" "
                hideHeader={true}
                showDatePicker={true}
                onDateRangeChange={handleCategoriesDateRangeChange}
                dateRange={categoriesDateRange}
                columns={[
                  { 
                    key: 'categoryName', 
                    label: 'Catégories', 
                    sortable: true
                  },
                  { 
                    key: 'sum', 
                    label: 'Somme',
                    sortable: true,
                    render: (amount, row) => (
                      <span style={{ 
                        color: row.amountColor,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        lineHeight: '19px',
                        letterSpacing: '0px',
                        textTransform: 'none',
                        opacity: 1
                      }}>
                        {amount}
                      </span>
                    )
                  }
                ]}
                data={categoriesData}
                maxHeight="500px"
                sortBy={categoriesSortBy}
                sortDirection={categoriesSortDirection}
                onSort={(sortBy, sortDirection) => {
                  setCategoriesSortBy(sortBy);
                  setCategoriesSortDirection(sortDirection);
                }}
                onRowClick={(row) => {
                  if (row.categoryDetailIds && row.categoryDetailIds.length > 0) {
                    // Build query params for Transactions page
                    const params = new URLSearchParams();
                    params.set('categoryDetailIds', row.categoryDetailIds.join(','));
                    
                    // Add selected caisse IDs
                    if (selectedCaisseIds.length > 0) {
                      params.set('caisseIds', selectedCaisseIds.join(','));
                    } else if (selectedCaisseIds.length === 0 && physiqueCaisses.length > 0) {
                      // Default to first physical caisse if none selected
                      params.set('caisseIds', physiqueCaisses[0].id.toString());
                    }
                    
                    // Add date range from the date picker, or default to today if not set
                    if (categoriesDateRange?.start && categoriesDateRange?.end) {
                      params.set('fromDate', categoriesDateRange.start.toLocaleDateString('en-CA'));
                      params.set('toDate', categoriesDateRange.end.toLocaleDateString('en-CA'));
                    } else {
                      // Default to today if no date range is set (matches backend default)
                      const today = new Date();
                      params.set('fromDate', today.toLocaleDateString('en-CA'));
                      params.set('toDate', today.toLocaleDateString('en-CA'));
                    }
                    
                    history.push(`/transactions?${params.toString()}`);
                  }
                }}
                containerPadding={0}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            <Rows
              title=" "
              hideHeader={true}
              showDatePicker={true}
              onDateRangeChange={handleCoffreDateRangeChange}
              dateRange={coffreDateRange}
              columns={[
                { 
                  key: 'createdAt', 
                  label: 'Date', 
                  sortable: true,
                  render: (value) => new Date(value).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })
                },
                { 
                  key: 'amount', 
                  label: 'Montant',
                  sortable: true,
                  render: (amount, row) => (
                    <span style={{ 
                      color: row.amountColor,
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      lineHeight: '19px',
                      letterSpacing: '0px',
                      textTransform: 'none',
                      opacity: 1
                    }}>
                      {amount}
                    </span>
                  )
                },
                { key: 'agentName', label: 'Par', sortable: true }
              ]}
              data={coffreTransactionsData}
              maxHeight="500px"
              sortBy={coffreSortBy}
              sortDirection={coffreSortDirection}
              onSort={(sortBy, sortDirection) => {
                setCoffreSortBy(sortBy);
                setCoffreSortDirection(sortDirection);
              }}
              containerPadding={0}
            />
          </Box>
        </Box>
      </Box>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Dashboard;

